import { useEffect, useState } from 'react';
import { m } from 'framer-motion';
import { useMsal } from '@azure/msal-react';
// @mui
import { Badge, Box, Divider, MenuItem, IconButton, Typography } from '@mui/material';

// components
import { varHover } from 'src/components/animate';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
// Direct path, not the application barrel: the barrel reaches this file through SiteLayout, so
// importing it from here is a cycle.
import {
  formatRingCentralNumber,
  useConnectRingCentral,
  useDisconnectRingCentral,
  useRingCentralConnection,
} from 'src/compass/application/messaging/ringCentral/useRingCentral';

type IUserInfo = {
  firstName?: string;
  name?: string;
  username?: string;
  email?: string;
};

export default function CasAccountPopover() {
  const { instance } = useMsal();
  const [user, setUser] = useState<IUserInfo | null>(null);
  const activeAccount = instance.getActiveAccount();

  // The user's own RingCentral connection, which their texts go out under (story 15619). Managed
  // here as well as from the compose windows, so it can be fixed before it is needed.
  const { permitted, connection, connected, expired } = useRingCentralConnection();
  const { connect, connecting } = useConnectRingCentral();
  const { disconnect, disconnecting } = useDisconnectRingCentral();
  const showRingCentral = permitted && !!connection?.isSetUp;

  useEffect(() => {
    if (activeAccount && activeAccount.username !== user?.username) {
      setUser({
        firstName: activeAccount?.name?.split(' ')[0],
        name: activeAccount.name,
        username: activeAccount.username,
      });
    } else if (!activeAccount && user !== null) {
      setUser(null);
    }
  }, [activeAccount, user]);

  const popover = usePopover();

  const handleLogout = async () => {
    instance.logoutRedirect();
  };

  const handleConnect = () => {
    popover.onClose();
    connect();
  };

  const handleDisconnect = () => {
    popover.onClose();
    disconnect();
  };

  return (
    <>
      {/* A dot only for a connection that lapsed - not for people who have never texted. */}
      <Badge
        color="warning"
        variant="dot"
        invisible={!showRingCentral || !expired}
        overlap="circular"
      >
        <IconButton
          component={m.button}
          whileTap="tap"
          whileHover="hover"
          variants={varHover(1.05)}
          onClick={popover.onOpen}
          sx={{
            height: 40,
            fontSize: 16,
            fontWeight: 'bold',
            color: '#999',

            ...(popover.open && {
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            }),
          }}
        >
          {user?.firstName}
        </IconButton>
      </Badge>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 220, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.username}
          </Typography>
        </Box>
        <Divider sx={{ borderStyle: 'dashed' }} />
        {showRingCentral && (
          <>
            <Box sx={{ px: 2, pt: 1.5 }}>
              <Typography variant="caption" color={expired ? 'warning.main' : 'text.secondary'}>
                {connected && `RingCentral: ${formatRingCentralNumber(connection?.fromNumber)}`}
                {expired && 'RingCentral connection expired'}
                {!connected && !expired && 'RingCentral not connected'}
              </Typography>
            </Box>
            {connected ? (
              <MenuItem onClick={handleDisconnect} disabled={disconnecting} sx={{ m: 1 }}>
                Disconnect RingCentral
              </MenuItem>
            ) : (
              <MenuItem
                onClick={handleConnect}
                disabled={connecting}
                sx={{ m: 1, ...(expired && { color: 'warning.main' }) }}
              >
                {expired ? 'Reconnect RingCentral' : 'Connect RingCentral'}
              </MenuItem>
            )}
            <Divider sx={{ borderStyle: 'dashed' }} />
          </>
        )}
        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Logout
        </MenuItem>
      </CustomPopover>
    </>
  );
}
